import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { loadPrefill, savePrefill } from "@/lib/prefill";
import { useCountry } from "@/lib/country";
import { getInternationalResources, INTL_COUNTRIES_WITH_RESOURCES } from "@/lib/internationalResources";
import SeoHead from "@/components/SeoHead";
import { NearbyPlacesFinder } from "@/components/NearbyPlacesFinder";
import { Library, Phone, ExternalLink, Users, ArrowRight, HeartHandshake, FlaskConical, Stethoscope, Home, Heart, Sun, AlertOctagon, Info } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

const FALLBACK_STATES = [
    { code: "ALL", name: "All states (national)" },
    { code: "TX", name: "Texas" },
];

/**
 * Resources — country-aware help finder.
 *
 * US users: state selector + national/state resources from backend.
 * Non-US users: country-specific curated list from
 *   /app/frontend/src/lib/internationalResources.js (no state picker).
 *
 * Country is read from the global Country/Language context (header
 * dropdown) — never displayed inline as tabs.
 */
export default function Resources() {
    const { country, meta, t } = useCountry();
    const [searchParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState(FALLBACK_STATES);
    // Read initial filters from URL (deep-links like
    // /help-finder?state=NY&category=home_health). Defaults keep prior
    // behavior so callers without params see "All states / All categories".
    const initialState = (searchParams.get("state") || "ALL").toUpperCase();
    const initialCategory = searchParams.get("category") || "all";
    const [category, setCategory] = useState(initialCategory);
    const [state, setState] = useState(initialState);
    // Telehealth/virtual now lives as its own TYPE chip ("telehealth"), so we
    // no longer track a separate remoteOnly toggle.
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const sid = getSessionId();

    const isUS = country === "US";
    const hasIntl = INTL_COUNTRIES_WITH_RESOURCES.includes(country);

    // Phase 4 data reuse — without an explicit ?state= deep-link, default the
    // filter to the state the caregiver already told us (saved ZIP/state);
    // and remember any state they pick here for next time.
    useEffect(() => {
        if (searchParams.get("state")) return;
        loadPrefill().then(p => {
            if (p.state) setState(s => (s === "ALL" ? p.state.toUpperCase() : s));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (state && state !== "ALL" && state !== initialState) savePrefill({ state });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Smooth-scroll to a section if the caller passed a hash (#guide-providers,
    // #help-today, …). Done once on mount after the page has rendered.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const hash = window.location.hash || "";
        if (!hash) return;
        const id = hash.slice(1);
        const t = setTimeout(() => {
            const el = document.getElementById(id) || document.querySelector(`[data-anchor="${id}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        api.get("/resources/categories").then(r => setCategories(r.data)).catch(() => { });
        api.get("/resources/states").then(r => setStates(r.data)).catch(() => { });
        api.get(`/profile/${sid}`).catch(() => { });
    }, [sid]);

    useEffect(() => {
        let active = true;
        setLoading(true);

        const fetchData = async () => {
            if (isUS) {
                // US: hit the backend resources directory.
                // Telehealth is a UX-level filter, not a server-side category
                // — we fetch ALL providers for the chosen state, then soft-
                // filter by virtual/online language client-side so caregivers
                // see telehealth options THAT SERVE THEIR STATE.
                const backendCategory = category === "telehealth" || category === "guide" ? "all" : category;
                try {
                    const r = await api.get(`/resources`, { params: { state, category: backendCategory } });
                    if (active) setItems(r.data);
                } catch {
                    if (active) setItems([]);
                } finally {
                    if (active) setLoading(false);
                }
            } else {
                // Non-US: curated client-side list.
                const list = hasIntl ? getInternationalResources(country, category) : [];
                if (active) {
                    setItems(list);
                    setLoading(false);
                }
            }
        };

        fetchData();
        return () => { active = false; };
    }, [state, category, country, isUS, hasIntl]);

    return (
        <div className="space-y-10 animate-fade-in-up">
            <SeoHead
                title="Dementia Care Resources — Find Help by State & Type | Memory Lane"
                description="A curated directory of dementia care resources — GUIDE providers, hospice, home health, respite, adult day, support groups, and telehealth — filterable by state. International caregivers covered in GB, IE, CA, AU, NZ, DE, FR, ES, MX."
                canonicalPath="/help-finder"
            />
            <PageHero
                kicker="Resources"
                kickerIcon={Library}
                title={t("resources.title")}
                subtitle={isUS
                    ? t("resources.subtitleUS")
                    : t("resources.subtitleIntl", { country: `${meta.flag} ${meta.label}` })}
            >
                {!isUS && !hasIntl && (
                    <p className="mt-3 text-sm text-coral-dark bg-coral-light/40 border border-coral-light rounded-2xl px-4 py-2 inline-block">
                        We&apos;re still building a directory for {meta.label}. National helplines below still work.
                    </p>
                )}
                {!isUS && hasIntl && (
                    <div className="mt-3"><ResourcesCountryBanner country={country} meta={meta} /></div>
                )}
            </PageHero>

            <Link
                to="/care-network"
                data-testid="resources-care-network-card"
                className="ml-card ml-card-hover p-5 md:p-6 flex items-start gap-4 bg-stage_late-light !border-stage_late-light hover:!border-stage_late-dark transition-colors"
            >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                    <Users strokeWidth={1.6} className="text-stage_late-dark" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-stage_late-dark">New</p>
                    <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        People who can help
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                        Private caregivers and local volunteers — request a match or browse free volunteer programs.
                    </p>
                </div>
                <ArrowRight size={20} strokeWidth={1.6} className="text-stage_late-dark mt-2 shrink-0" />
            </Link>

            {/* GUIDE-participating providers card removed — GUIDE is now a
                category chip in the TYPE filter below. Selecting it renders
                the curated GUIDE provider list inline with the state filter
                already applied. */}

            {/* What's the difference? — Plain-English glossary card that
                explains hospice vs home health vs respite vs adult day vs
                GUIDE in caregiver-friendly language. Clicking a row sets the
                category filter below so they can immediately see what's
                available locally. */}
            {isUS && (
                <ServicesExplainerCard
                    category={category}
                    onPick={(k) => {
                        setCategory(k);
                        setTimeout(() => {
                            const el = document.querySelector('[data-testid="resources-list"]');
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 80);
                    }}
                />
            )}

            {/* GUIDE care services tile block removed — duplicates the
                "What's the difference?" explainer card above which already
                lets caregivers filter by home health / hospice / respite /
                adult day in plain English. */}

            {/* Community Partners moved out of Resources — it now lives at
                /wellness because partner help is fundamentally a caregiver
                self-care concern, not a clinical resource. */}

            {/* Treatments + active trials — FDA/EMA approved meds (country-
                aware), non-medication interventions, and live ClinicalTrials.gov
                recruiting studies with deep links to learn more. */}
            <Link
                to="/treatments"
                data-testid="resources-treatments-card"
                className="ml-card ml-card-hover p-5 md:p-6 flex items-start gap-4 bg-coral-light/40 !border-coral-softer hover:!border-coral-dark transition-colors"
            >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                    <FlaskConical strokeWidth={1.6} className="text-coral-dark" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-coral-dark">Treatments & trials</p>
                    <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        What&apos;s available where you live
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                        Approved medications (Leqembi, Kisunla, Donepezil…), non-drug interventions, and
                        active clinical trials recruiting near you — with deep links to learn more on each.
                    </p>
                </div>
                <ArrowRight size={20} strokeWidth={1.6} className="text-coral-dark mt-2 shrink-0" />
            </Link>

            {/* Who-pays card removed from Resources — it already has its own
                sidebar tab at /who-pays, so showing it again here was redundant. */}

            {/* Find care near you — address → Google Maps deep links for
                respite / adult day programs / home health / neurology /
                geriatricians / memory care / support groups / elder law. */}
            <NearbyPlacesFinder />

            <section className="ml-card p-5 md:p-6 space-y-4" data-testid="resources-filters">
                {/* State selector — US only */}
                {isUS && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <label className="text-sm font-semibold text-[#4C5551] uppercase tracking-wider sm:w-20" htmlFor="state-select">{t("resources.state")}</label>
                        <select
                            id="state-select"
                            value={state}
                            onChange={e => setState(e.target.value)}
                            data-testid="resources-state"
                            className="ml-input !min-h-[48px] !py-2.5 !px-3 !text-base sm:max-w-xs"
                        >
                            {states.map(s => (
                                <option key={s.code} value={s.code}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="text-sm font-semibold text-[#4C5551] uppercase tracking-wider sm:w-20">{t("resources.type")}</span>
                    <div className="flex flex-wrap gap-2">
                        {categories.map(c => (
                            <button
                                key={c.key}
                                onClick={() => setCategory(c.key)}
                                data-testid={`resource-cat-${c.key}`}
                                className={`px-4 py-2 rounded-2xl text-sm font-medium border transition-colors ${category === c.key
                                    ? "bg-teal text-white border-teal"
                                    : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                    }`}
                            >{c.label}</button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" data-testid="resources-list">
                {loading && <p className="text-[#4C5551]">{t("common.loading")}</p>}

                {/* GUIDE chip selected — render the curated participant list
                    (state-aware) directly in the results stream instead of
                    hitting the resources directory. This is how caregivers
                    see "what GUIDE is in my state". */}
                {!loading && isUS && category === "guide" && (
                    <div className="md:col-span-2" data-testid="resources-guide-inline">
                        <GuideProvidersCard initialState={state === "ALL" ? "" : state} onPickState={setState} />
                    </div>
                )}

                {(() => {
                    if (loading) return null;
                    if (category === "guide") return null; // handled above
                    const VIRTUAL_RE = /\b(virtual|telehealth|telemedicine|online|video visit|video-visit|remote|tele-?[a-z]+|web-?based)\b/i;
                    // Telehealth chip = soft-filter by virtual language across
                    // every entry (since most agencies don't tag themselves
                    // "telehealth" cleanly in the directory).
                    const virtualMode = category === "telehealth";
                    const baseItems = virtualMode
                        ? items.filter(r => VIRTUAL_RE.test(`${r.name || ""} ${r.notes || ""} ${r.eligibility || ""}`))
                        : items;
                    const visible = baseItems;
                    if (visible.length === 0) {
                        return (
                            <p className="text-[#4C5551]" data-testid="resources-empty">
                                {virtualMode
                                    ? "No telehealth / virtual matches with your current filters. Try clearing the state filter."
                                    : t("common.noResults")}
                            </p>
                        );
                    }
                    return visible.map(r => {
                    const cardOpensWeb = !!r.website;
                    const openCard = () => {
                        if (r.website) {
                            track(EVENTS.RESOURCE_CLICK, {
                                resource_id: r.id,
                                resource_name: r.name,
                                resource_category: r.category,
                                resource_state: r.state || r.country,
                                target: "card",
                            });
                            window.open(r.website, "_blank", "noopener,noreferrer");
                        }
                    };
                    return (
                        <article
                            key={r.id}
                            onClick={cardOpensWeb ? openCard : undefined}
                            onKeyDown={cardOpensWeb ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCard(); } } : undefined}
                            role={cardOpensWeb ? "link" : undefined}
                            tabIndex={cardOpensWeb ? 0 : undefined}
                            className={`ml-card ml-card-hover p-6 ${cardOpensWeb ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal" : ""}`}
                            data-testid={`resource-${r.id}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <p className="text-xs uppercase tracking-wider font-semibold text-teal">
                                    {r.category.replace(/_/g, ' ')}
                                </p>
                                {(r.state === "ALL" || (!isUS && r.country)) && (
                                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-2xl bg-coral-light text-coral-dark">
                                        {isUS ? t("resources.national") : meta.label}
                                    </span>
                                )}
                            </div>
                            <h3 className="text-xl font-heading font-medium text-[#1F2624]">{r.name}</h3>
                            <p className="mt-2 text-sm text-[#4C5551] leading-relaxed">{r.notes}</p>
                            {r.eligibility && (
                                <p className="mt-2 text-xs text-[#4C5551]"><strong className="text-[#1F2624]">Eligibility:</strong> {r.eligibility}</p>
                            )}
                            <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                {r.phone && (
                                    <a href={`tel:${r.phone.replace(/\D/g, '')}`} data-testid={`resource-call-${r.id}`}
                                        onClick={() => track(EVENTS.RESOURCE_CLICK, {
                                            resource_id: r.id, resource_name: r.name,
                                            resource_category: r.category, target: "phone",
                                        })}
                                        className="ml-btn-primary !min-h-[44px] !py-2 !px-4 text-sm">
                                        <Phone size={16} strokeWidth={1.6} /> {r.phone}
                                    </a>
                                )}
                                {r.website && (
                                    <a href={r.website} target="_blank" rel="noreferrer" data-testid={`resource-web-${r.id}`}
                                        onClick={() => track(EVENTS.RESOURCE_CLICK, {
                                            resource_id: r.id, resource_name: r.name,
                                            resource_category: r.category, target: "website",
                                        })}
                                        className="!min-h-[44px] !py-2 !px-4 text-sm inline-flex items-center justify-center gap-2 rounded-2xl font-medium bg-teal-soft text-teal-dark hover:bg-teal/20 transition-colors">
                                        <ExternalLink size={16} strokeWidth={1.6} /> Visit
                                    </a>
                                )}
                            </div>
                        </article>
                    );
                });
                })()}
            </section>
        </div>
    );
}


/* ---------------- GUIDE-participating providers ----------------
 * FULL CMS participant directory (497 organizations, all states +
 * territories), parsed from the official participant XLSX with each
 * provider's website, phone, HQ city, states served, and telehealth
 * availability. The 103KB dataset is lazy-loaded on first interaction
 * so it never weighs down the initial page bundle.
 *
 * Source of truth: https://www.cms.gov/priorities/innovation/innovation-models/guide
 * Participant XLSX: https://www.cms.gov/priorities/innovation/files/guide-participant-list.xlsx
 */
const CMS_GUIDE_PAGE = "https://www.cms.gov/priorities/innovation/innovation-models/guide";
const CMS_GUIDE_XLSX = "https://www.cms.gov/priorities/innovation/files/guide-participant-list.xlsx";

const PAGE_SIZE = 30;

function GuideProvidersCard({ initialState = "", onPickState }) {
    const [open, setOpen] = useState(Boolean(initialState));
    const [stateFilter, setStateFilter] = useState((initialState || "").toUpperCase());
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [data, setData] = useState(null);

    useEffect(() => {
        import("@/data/guideParticipants.json").then(m => setData(m.default || m)).catch(() => setData({ participants: [], states: [], count: 0 }));
    }, []);

    const participants = data?.participants || [];
    const stateCounts = React.useMemo(() => {
        const counts = {};
        for (const p of participants) for (const s of p.states) counts[s] = (counts[s] || 0) + 1;
        return counts;
    }, [participants]);

    const filtered = React.useMemo(() => {
        let list = participants;
        if (stateFilter) list = list.filter(p => p.states.includes(stateFilter));
        const q = query.trim().toLowerCase();
        if (q) list = list.filter(p => p.name.toLowerCase().includes(q) || (p.city || "").toLowerCase().includes(q));
        return list;
    }, [participants, stateFilter, query]);

    const shown = filtered.slice(0, limit);

    return (
        <section
            id="guide-providers"
            data-anchor="guide-providers"
            className="ml-card p-0 bg-gradient-to-br from-stage_early/30 via-cream-light to-teal-soft/30 border border-stage_early-soft overflow-hidden"
            data-testid="resources-guide-providers"
        >
            <div className="flex items-start gap-4 p-5 md:p-6">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                    <Stethoscope strokeWidth={1.6} className="text-stage_early-dark" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-stage_early-dark">
                        Medicare GUIDE Model · United States
                    </p>
                    <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                        Find a GUIDE provider near you
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551] leading-relaxed" data-testid="resources-guide-intro">
                        The full CMS directory — {data?.count || 497} health systems and dementia-care groups,
                        every state and territory, with websites and phone numbers.
                        {data?.lastUpdated ? ` CMS list last updated ${data.lastUpdated}.` : ""}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                        <label className="text-xs uppercase tracking-wider font-semibold text-stage_early-dark mr-1">
                            Your state
                        </label>
                        <select
                            value={stateFilter}
                            onChange={(e) => {
                                const v = e.target.value;
                                setStateFilter(v);
                                setLimit(PAGE_SIZE);
                                if (v && onPickState) onPickState(v);
                                if (!open) setOpen(true);
                            }}
                            data-testid="resources-guide-state-filter"
                            className="px-3 py-1.5 rounded-2xl bg-white border border-stage_early text-stage_early-dark text-sm font-medium focus:outline-none focus:ring-2 focus:ring-stage_early/40"
                        >
                            <option value="">All states ({data?.count || "…"})</option>
                            {(data?.states || []).map(s => (
                                <option key={s} value={s}>{s} ({stateCounts[s] || 0})</option>
                            ))}
                        </select>
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setLimit(PAGE_SIZE); if (!open) setOpen(true); }}
                            placeholder="Search name or city"
                            data-testid="resources-guide-search"
                            className="px-3.5 py-1.5 rounded-2xl bg-white border border-cream-dark text-sm w-44 focus:outline-none focus:ring-2 focus:ring-stage_early/40"
                        />
                        <a
                            href={CMS_GUIDE_PAGE}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="resources-guide-cms-page"
                            className="!min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5 rounded-2xl font-medium bg-white border border-stage_early text-stage_early-dark hover:bg-stage_early/20 transition-colors"
                        >
                            <ExternalLink size={14} strokeWidth={1.7} />
                            Official CMS page
                        </a>
                        <button
                            type="button"
                            onClick={() => setOpen(o => !o)}
                            data-testid="resources-guide-toggle"
                            className="ml-btn-primary !min-h-[40px] !px-4 !py-2 !text-sm inline-flex items-center gap-1.5"
                        >
                            {open ? "Hide directory" : `Browse ${stateFilter ? `${filtered.length} in ${stateFilter}` : "the full directory"}`}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="px-5 md:px-6 pb-5">
                    {!data && (
                        <p className="text-sm text-[#6E7A74] italic" data-testid="resources-guide-loading">Loading the directory…</p>
                    )}
                    {data && (
                        <>
                            <p className="text-xs text-[#6E7A74] mb-2" data-testid="resources-guide-count">
                                {filtered.length} provider{filtered.length === 1 ? "" : "s"}
                                {stateFilter ? ` serving ${stateFilter}` : ""}{query.trim() ? ` matching “${query.trim()}”` : ""}
                            </p>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="resources-guide-list">
                                {filtered.length === 0 && (
                                    <li className="text-sm text-[#6E7A74] italic col-span-2" data-testid="resources-guide-empty">
                                        No participants listed{stateFilter ? ` for ${stateFilter}` : ""} yet. New organizations
                                        join each cycle — check the official CMS page above, and consider telehealth
                                        providers that serve neighboring states.
                                    </li>
                                )}
                                {shown.map((p) => (
                                    <li
                                        key={`${p.name}-${p.hq || ""}`}
                                        className="rounded-2xl border border-cream-dark bg-white px-3.5 py-2.5"
                                        data-testid="resources-guide-participant"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <span className="text-sm font-medium text-[#1F2624] leading-snug">{p.name}</span>
                                            {p.telehealth && (
                                                <span className="shrink-0 text-[10px] uppercase tracking-wide font-semibold text-teal-dark bg-teal-soft rounded-2xl px-2 py-0.5">Telehealth</span>
                                            )}
                                        </div>
                                        <p className="mt-0.5 text-xs text-[#6E7A74]">
                                            {(() => {
                                                const loc = [p.city, p.hq].filter(Boolean).join(", ");
                                                const serves = p.states.length > 8
                                                    ? `serves ${p.states.length} states${stateFilter ? ` incl. ${stateFilter}` : ""}`
                                                    : p.states.length > 1 ? `serves ${p.states.join(", ")}` : "";
                                                return [loc, serves].filter(Boolean).join(" · ");
                                            })()}
                                        </p>
                                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
                                            {p.website && (
                                                <a href={p.website} target="_blank" rel="noopener noreferrer"
                                                    className="text-teal-dark hover:underline inline-flex items-center gap-1">
                                                    <ExternalLink size={11} strokeWidth={1.8} /> Website
                                                </a>
                                            )}
                                            {p.phone && (
                                                <a href={`tel:${p.phone.replace(/[^\d+]/g, "")}`} className="text-[#4C5551] hover:text-teal-dark">
                                                    {p.phone}
                                                </a>
                                            )}
                                            {!p.website && !p.phone && (
                                                <span className="text-[#9CA6A1]">See the CMS list for contact details</span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {filtered.length > shown.length && (
                                <button
                                    type="button"
                                    onClick={() => setLimit(l => l + 60)}
                                    data-testid="resources-guide-show-more"
                                    className="mt-3 w-full rounded-full bg-white border border-cream-dark text-sm font-medium text-[#1F2624] py-2.5 hover:bg-cream-light transition-colors"
                                >
                                    Show more ({filtered.length - shown.length} remaining)
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}

            <p className="px-5 md:px-6 pb-5 text-xs text-[#6E7A74]" data-testid="resources-guide-disclaimer">
                Directory parsed from the official CMS participant list ({data?.lastUpdated || "April 2026"}).
                Some participants serve only part of a state, and new organizations join each cycle — the{" "}
                <a href={CMS_GUIDE_XLSX} className="text-teal-dark underline" target="_blank" rel="noopener noreferrer">CMS spreadsheet</a>{" "}
                is the authoritative source. Once you find a provider, ask if they&apos;ll accept your GUIDE eligibility packet
                (use the share link from <Link to="/guide" className="text-teal-dark underline">your GUIDE section</Link>).
            </p>
        </section>
    );
}

/* ---------------- "What's the difference?" services explainer ----------------
 * The single biggest source of caregiver confusion the Memory Lane team
 * hears: "is what my dad needs hospice or home health? Is GUIDE the same
 * as adult day care?" This component answers that question in plain English
 * — one row per service type, with what it is, who it's for, and who pays.
 * Clicking a row filters the directory below so the caregiver can
 * immediately see local options.
 */
const SERVICE_DEFS = [
    {
        key: "home_health",
        label: "Home health",
        Icon: Home,
        tone: "bg-teal-soft text-teal-dark border-teal-light",
        what: "Skilled nursing or therapy delivered at home — wound care, IV meds, PT/OT, RN visits.",
        who: "After a hospital stay, or when your loved one is homebound and needs medical care a family member can't do alone.",
        pays: "Medicare Part A/B covers it when ordered by a doctor and delivered by a Medicare-certified agency.",
    },
    {
        key: "hospice",
        label: "Hospice",
        Icon: Heart,
        tone: "bg-coral-light text-coral-dark border-coral-softer",
        what: "Comfort-focused care for the last months of life — symptom control, family support, chaplain, social worker, RN visits, equipment.",
        who: "When two physicians certify a prognosis of ~6 months or less and the family chooses comfort over more treatment.",
        pays: "Medicare Hospice Benefit covers nearly all of it. Medicaid and most private insurers match.",
    },
    {
        key: "respite",
        label: "Respite care",
        Icon: Sun,
        tone: "bg-yellow-light text-yellow-dark border-yellow",
        what: "Short, planned break for you — a few hours, a weekend, or a week. Can be in-home (someone comes to your house) or a short stay at a facility.",
        who: "Any caregiver who needs to sleep, work, attend an appointment, or simply breathe. Not just for emergencies.",
        pays: "Some via Medicaid waivers, VA, GUIDE (up to $2,500/yr), Alzheimer's grants, or private pay. Often the most under-used benefit.",
    },
    {
        key: "adult_day_care",
        label: "Adult day program",
        Icon: Users,
        tone: "bg-stage_early/30 text-stage_early-dark border-stage_early-soft",
        what: "Daytime social + supervised care at a community center — meals, activities, light health monitoring. Pickup/dropoff sometimes included.",
        who: "Middle-stage dementia, when your loved one is still mobile but unsafe to be alone, and you want them engaged with other people.",
        pays: "Varies: Medicaid waivers, VA Aid & Attendance, sliding-scale fees, occasionally private LTC insurance.",
    },
    {
        key: "guide",
        label: "GUIDE",
        Icon: Stethoscope,
        tone: "bg-sage-light text-sage-dark border-sage-soft",
        what: "Medicare's new dementia care navigation program (started 2024). A care navigator, 24/7 helpline, caregiver training, and up to $2,500/yr in respite — all coordinated through one health system.",
        who: "Anyone with dementia who has traditional Medicare (not Medicare Advantage) and a participating provider in their area.",
        pays: "Medicare. No copay for GUIDE services. Use our GUIDE eligibility check above to confirm.",
    },
];

function ServicesExplainerCard({ category, onPick }) {
    // Collapsed by default — caregivers can tap "Show" to expand. Keeps the
    // page short and lets the curated provider list breathe.
    const [open, setOpen] = useState(false);
    return (
        <section
            id="services-explainer"
            data-anchor="services-explainer"
            className="ml-card p-0 bg-white border border-cream-dark overflow-hidden"
            data-testid="resources-services-explainer"
        >
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid="services-explainer-toggle"
                className="w-full flex items-start justify-between gap-3 px-5 md:px-6 py-4 hover:bg-cream-light/60 transition-colors text-left"
                aria-expanded={open}
            >
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                        <Info size={18} strokeWidth={1.7} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Plain English</p>
                        <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                            What&apos;s the difference between hospice, home health, respite &amp; GUIDE?
                        </h2>
                        <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                            Caregivers ask us this every week. Tap a service to filter local providers below.
                        </p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-cream-light border border-cream-dark text-[#1F2624] shrink-0">
                    {open ? "Hide" : "Show"}
                </span>
            </button>

            {open && (
                <div className="px-5 md:px-6 pb-5">
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="services-explainer-list">
                        {SERVICE_DEFS.map(({ key, label, Icon, tone, what, who, pays }) => {
                            const active = category === key;
                            return (
                                <li
                                    key={key}
                                    data-testid={`services-explainer-row-${key}`}
                                    className={`rounded-2xl border p-4 transition-all ${
                                        active ? "border-teal-dark bg-teal-soft/30" : "border-cream-dark bg-white hover:bg-cream-light"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-2xl border ${tone} shrink-0`}>
                                            <Icon size={18} strokeWidth={1.7} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-heading text-lg text-[#1F2624]">{label}</p>
                                            <dl className="mt-1.5 space-y-1 text-sm">
                                                <div><dt className="inline text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">What it is · </dt><dd className="inline text-[#1F2624]">{what}</dd></div>
                                                <div><dt className="inline text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Who it&apos;s for · </dt><dd className="inline text-[#4C5551]">{who}</dd></div>
                                                <div><dt className="inline text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">Who pays · </dt><dd className="inline text-[#4C5551]">{pays}</dd></div>
                                            </dl>
                                            {key !== "guide" && (
                                                <button
                                                    type="button"
                                                    onClick={() => onPick(key)}
                                                    data-testid={`services-explainer-filter-${key}`}
                                                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-medium bg-white border border-cream-dark text-[#1F2624] hover:border-teal-dark hover:text-teal-dark transition-colors"
                                                >
                                                    Show {label.toLowerCase()} near me <ArrowRight size={11} strokeWidth={2} />
                                                </button>
                                            )}
                                            {key === "guide" && (
                                                <Link
                                                    to="/guide"
                                                    data-testid="services-explainer-guide-link"
                                                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-medium bg-sage-light border border-sage-soft text-sage-dark hover:bg-sage hover:text-white transition-colors"
                                                >
                                                    Check my GUIDE eligibility <ArrowRight size={11} strokeWidth={2} />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </section>
    );
}


/* Country welcome banner for Resources — mirrors the landing hero strip
 * but with more room. Shows the top 3 curated local resources for non-US
 * caregivers with a sense-of-place tagline ("Dementia care across X — we
 * know A, B, and C"). Hidden for US (the long-form CMS GUIDE block already
 * does the same job) and for countries without a curated directory. */
function ResourcesCountryBanner({ country, meta }) {
    const top = getInternationalResources(country).slice(0, 3);
    if (!top.length) return null;
    // Region-aware tagline keyed off the country code. Falls back to a
    // generic "Dementia care across X" line if the code isn't covered.
    const TAGLINES = {
        GB: "NHS-aligned dementia resources in the United Kingdom",
        IE: "Dementia care across Ireland — HSE, Alzheimer Society & Family Carers",
        CA: "Dementia care across Canada — Alzheimer Society + provincial First Link",
        AU: "Dementia care across Australia — My Aged Care, Carer Gateway & Dementia Australia",
        NZ: "Dementia support across Aotearoa New Zealand",
        DE: "Demenzhilfe in Deutschland — Pflegekasse, Pflegestützpunkte & Alzheimer-Telefon",
        FR: "Soutien à la démence en France — APA, CLIC & France Alzheimer",
        ES: "Apoyo a la demencia en España — SAAD, CEAFA y AFAs locales",
        MX: "Apoyo de demencia en México — IMSS, ISSSTE, INAPAM y FEDMA",
    };
    const tagline = TAGLINES[country] || `Dementia care across ${meta.label}`;
    return (
        <div
            data-testid="resources-country-banner"
            className="mt-4 rounded-2xl bg-gradient-to-br from-teal-soft/40 via-cream-light to-stage_early/30 border border-teal-light px-4 py-3 md:px-5 md:py-4"
        >
            <p className="text-sm md:text-base text-[#1F2624] leading-snug">
                <span aria-hidden="true" className="mr-1.5 text-base md:text-lg">{meta.flag}</span>
                <span className="font-heading font-medium">{tagline}.</span>
            </p>
            <p className="text-xs md:text-sm text-[#4C5551] mt-1.5">
                We&apos;ve curated:{" "}
                {top.map((r, i) => (
                    <React.Fragment key={r.id}>
                        {i > 0 && (i === top.length - 1 ? " and " : ", ")}
                        <a
                            href={r.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`resources-country-banner-link-${r.id}`}
                            className="text-teal-dark underline hover:text-teal whitespace-nowrap"
                        >
                            {r.name}
                        </a>
                    </React.Fragment>
                ))}
                .
            </p>
        </div>
    );
}
